import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import React, { useState, useRef, useCallback } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { resizeImage } from '../Statefull/Utils/resizeImage';
import { uploadImageWithPresignedUrl } from '../HelperFunctions/s3Service';
import { useContext } from 'react';
import { HimmaContext } from '../../context/HimmaContext';

interface ImageUploadFieldProps {
    name: string;
    label: string;
    multiple?: boolean;
    maxFiles?: number;
    recommendedText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
    name,
    label,
    multiple = false,
    maxFiles = 1,
    recommendedText,
}) => {
    const [field, meta] = useField(name);
    const form = useFormikContext<any>();
    const apiClient = useContext(HimmaContext);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    // Handle files from both input and drop
    const processFiles = async (files: File[]) => {
        form.setFieldTouched(name, true);

        if (files.length === 0) {
            form.setFieldError(name, `Please upload ${label.toLowerCase()}`);
            return;
        }

        const existing = multiple ? field.value || [] : [];
        if (multiple && existing.length + files.length > maxFiles) {
            form.setFieldError(name, `Maximum ${maxFiles} ${label.toLowerCase()} images allowed`);
            return;
        }

        try {
            if (!apiClient) {
                throw new Error('API client not available');
            }
            const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
            const uploaded = await Promise.all(resizedFiles.map(file => uploadImageWithPresignedUrl(file, apiClient)));
            const urls = uploaded.map(res => res.url);

            const newValue = multiple ? [...existing, ...urls] : urls[0];
            form.setFieldValue(name, newValue);
            form.setFieldError(name, undefined);
        } catch (err) {
            console.error(err);

            const errorMessage =
                typeof err === 'string'
                    ? err
                    : err instanceof Error
                        ? err.message
                        : `Error uploading ${label.toLowerCase()}`;

            form.setFieldError(name, errorMessage);
        }
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        await processFiles(files);
    };

    // Drag and drop handlers
    const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragActive(false);
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        await processFiles(files);
    }, [processFiles]);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!isDragActive) setIsDragActive(true);
    };
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const handleClear = () => {
        form.setFieldValue(name, multiple ? [] : null);
        form.setFieldError(name, undefined);
        // Reset the file input value so the same file can be selected again
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const fileInputId = `${name}-input`;
    const hasValue = multiple ? Array.isArray(field.value) && field.value.length > 0 : !!field.value;
    // Ensure native input gets cleared when Formik value is cleared (e.g., on Reset)
    React.useEffect(() => {
        const isEmpty = multiple ? !Array.isArray(field.value) || field.value.length === 0 : !field.value;
        if (isEmpty && inputRef.current) {
            inputRef.current.value = '';
        }
    }, [field.value, multiple]);
    const uploadedImages = React.useMemo(() => {
        if (!field.value) return [];

        if (multiple) {
            // Normalize: extract url from objects if needed
            return (field.value as any[]).map(img =>
                typeof img === 'string' ? img : img?.url || ''
            ).filter(Boolean);
        }

        // Single image case
        const single = field.value;
        if (typeof single === 'string') return [single];
        if (typeof single === 'object' && single?.url) return [single.url];
        return [];
    }, [field.value, multiple]);

    return (
        <FormControl error={Boolean(meta.touched && meta.error)} fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    {/* Drag and Drop Area */}
                    <Box
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        sx={{
                            border: isDragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
                            borderRadius: 2,
                            p: 2,
                            mb: 0,
                            background: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            transition: 'border 0.2s, background 0.2s',
                            textAlign: 'center',
                            cursor: 'pointer',
                            minHeight: 180,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={() => inputRef.current?.click()}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple={multiple}
                            onChange={handleChange}
                            style={{ display: 'none' }}
                            id={fileInputId}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {isDragActive
                                ? 'Drop Image Here...'
                                : 'Drag & Drop Image Here or Click to Upload'}
                        </Typography>
                        {/* Image Thumbnails */}
                        {hasValue && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 1 }}>
                                    {uploadedImages.map((url: string, idx: number) => (
                                        <Box key={idx} sx={{ position: 'relative' }}>
                                            <img
                                                src={url}
                                                alt={`${label} ${idx + 1}`}
                                                style={{
                                                    width: multiple ? 120 : 120,
                                                    height: multiple ? 60 : 60,
                                                    objectFit: 'cover',
                                                    borderRadius: 4,
                                                    border: '1px solid #ddd',
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={e => { e.stopPropagation(); handleClear(); }}
                                        sx={{ height: '36px', textTransform: 'uppercase', fontWeight: 600 }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={e => { e.stopPropagation(); setPreviewOpen(true); }}
                                        sx={{ height: '36px', textTransform: 'uppercase', fontWeight: 600 }}
                                    >
                                        Preview
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {!hasValue && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No file chosen
                            </Typography>
                        )}
                    </Box>
                    {/* Recommended Text */}
                    {recommendedText && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                        >
                            {recommendedText}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Preview {label}
                    <IconButton
                        aria-label="close"
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
                        <Tab label="Mobile Image" />
                        <Tab label="Web Image" />
                    </Tabs>

                    {uploadedImages.map((url: string, idx: number) => (
                        <Box key={idx} sx={{ display: tabIndex === 0 ? 'block' : 'none' }}>
                            {/* Mobile Preview */}
                            <Box sx={{ maxWidth: 400, mx: 'auto', }}>
                                <img
                                    src={url}
                                    alt={`Mobile Preview ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}

                    {uploadedImages.map((url: string, idx: number) => (
                        <Box key={idx} sx={{ display: tabIndex === 1 ? 'block' : 'none' }}>
                            {/* Web Preview */}
                            <Box sx={{ width: '100%', }}>
                                <img
                                    src={url}
                                    alt={`Web Preview ${idx + 1}`}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
        </FormControl>
    );
};

export default ImageUploadField;
